import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useLearningPathNodes, useLearningPathProgress } from './useLearningPaths';
import { useAuth } from './useAuth';

interface LearningPathContextType {
  // Path information
  pathId?: string;
  disciplineId?: string;
  subdisciplineSlug?: string;
  
  // Current state
  currentNodeId?: string;
  currentIndex: number;
  
  // Path data
  nodes: any[];
  totalNodes: number;
  
  // Progress tracking
  userProgress: Record<string, { completed: boolean; timeSpent: number; rating?: number }>;
  completedNodes: number;
  availableNodes: number;
  lockedNodes: number;
  
  // Navigation helpers
  canNavigatePrevious: boolean;
  canNavigateNext: boolean;
  previousNode?: any;
  nextNode?: any;
  
  // Path metadata
  estimatedTimeRemaining: number;
  difficultyProgression: number[];
  pathType?: string;
  
  // Actions
  updateProgress: (nodeId: string, progress: { completed?: boolean; timeSpent?: number; rating?: number }) => void;
  navigateToNode: (nodeId: string) => void;
  markNodeComplete: (nodeId: string) => void;
}

const LearningPathContext = createContext<LearningPathContextType | null>(null);

interface LearningPathProviderProps {
  children: ReactNode;
  pathId?: string;
  currentNodeId?: string;
  customNodes?: any[];
}

export const LearningPathProvider: React.FC<LearningPathProviderProps> = ({
  children,
  pathId: providedPathId,
  currentNodeId: providedCurrentNodeId,
  customNodes
}) => {
  const params = useParams();
  const location = useLocation();
  const { user } = useAuth();
  
  // Determine path and node IDs from context
  const pathId = providedPathId || extractPathIdFromRoute(location.pathname, params);
  const currentNodeId = providedCurrentNodeId || extractNodeIdFromRoute(location.pathname, params);
  
  // Fetch path data
  const { data: pathNodes = [] } = useLearningPathNodes(pathId || '');
  const { data: userProgress = {} } = useLearningPathProgress(pathId || '');
  
  // Use custom nodes if provided, otherwise use fetched nodes
  const nodes = customNodes || pathNodes;
  
  // Find current node index
  const currentIndex = currentNodeId 
    ? nodes.findIndex(node => node.node_id === currentNodeId || node.id === currentNodeId)
    : 0;
  
  // Calculate progress metrics
  const completedNodes = Object.values(userProgress).filter(p => p.completed).length;
  const totalNodes = nodes.length;
  
  // Navigation helpers
  const canNavigatePrevious = currentIndex > 0;
  const canNavigateNext = currentIndex < totalNodes - 1;
  const previousNode = canNavigatePrevious ? nodes[currentIndex - 1] : undefined;
  const nextNode = canNavigateNext ? nodes[currentIndex + 1] : undefined;
  
  // Calculate available and locked nodes
  const { availableCount, lockedCount } = calculateNodeAccessibility(nodes, userProgress, currentIndex);
  
  // Calculate time and difficulty metrics
  const estimatedTimeRemaining = calculateRemainingTime(nodes, userProgress, currentIndex);
  const difficultyProgression = nodes.map(node => node.difficulty_level || 3);
  
  // Local progress state for immediate updates
  const [localProgress, setLocalProgress] = useState<Record<string, any>>({});
  const mergedProgress = { ...userProgress, ...localProgress };
  
  const updateProgress = (nodeId: string, progress: any) => {
    setLocalProgress(prev => ({
      ...prev,
      [nodeId]: { ...prev[nodeId], ...progress }
    }));
    
    // TODO: Sync with backend
    console.log('Updating progress for node:', nodeId, progress);
  };
  
  const navigateToNode = (nodeId: string) => {
    // TODO: Implement navigation logic
    console.log('Navigate to node:', nodeId);
  };
  
  const markNodeComplete = (nodeId: string) => {
    updateProgress(nodeId, { completed: true });
  };
  
  // Extract discipline and subdiscipline information
  const { disciplineId, subdisciplineSlug, pathType } = extractRouteMetadata(location.pathname, params);

  const contextValue: LearningPathContextType = {
    pathId,
    disciplineId,
    subdisciplineSlug,
    currentNodeId,
    currentIndex: Math.max(0, currentIndex),
    nodes,
    totalNodes,
    userProgress: mergedProgress,
    completedNodes,
    availableNodes: availableCount,
    lockedNodes: lockedCount,
    canNavigatePrevious,
    canNavigateNext,
    previousNode,
    nextNode,
    estimatedTimeRemaining,
    difficultyProgression,
    pathType,
    updateProgress,
    navigateToNode,
    markNodeComplete
  };

  return (
    <LearningPathContext.Provider value={contextValue}>
      {children}
    </LearningPathContext.Provider>
  );
};

export const useLearningPathContext = () => {
  const context = useContext(LearningPathContext);
  return context; // Return null if not in a learning path context
};

// Helper functions
function extractPathIdFromRoute(pathname: string, params: any): string | undefined {
  // Extract path ID from various route patterns
  if (pathname.includes('/learning-paths/')) {
    return pathname.split('/learning-paths/')[1]?.split('/')[0];
  }
  
  // For study routes, create a synthetic path ID based on discipline/subdiscipline
  if (pathname.includes('/study/') || pathname.includes('/philosophy/') || pathname.includes('/theology/')) {
    const slug = params.slug;
    if (slug) {
      return `synthetic-path-${slug}`;
    }
  }
  
  return undefined;
}

function extractNodeIdFromRoute(pathname: string, params: any): string | undefined {
  // Extract node ID from content view routes
  if (pathname.includes('/content/view/')) {
    return pathname.split('/content/view/')[1]?.split('/')[0];
  }
  
  // For study routes, the slug might represent a node
  if (pathname.includes('/study/') || pathname.includes('/philosophy/') || pathname.includes('/theology/')) {
    return params.slug;
  }
  
  return undefined;
}

function extractRouteMetadata(pathname: string, params: any) {
  let disciplineId: string | undefined;
  let subdisciplineSlug: string | undefined;
  let pathType: string | undefined;
  
  // Determine discipline from route
  if (pathname.includes('/philosophy')) {
    disciplineId = 'philosophy';
    pathType = 'discipline';
    subdisciplineSlug = params.slug;
  } else if (pathname.includes('/theology')) {
    disciplineId = 'theology';
    pathType = 'discipline';
    subdisciplineSlug = params.slug;
  } else if (pathname.includes('/human-inquiry')) {
    disciplineId = 'human-inquiry';
    pathType = 'pillar';
  } else if (pathname.includes('/world-itself')) {
    disciplineId = 'world-itself';
    pathType = 'pillar';
  } else if (pathname.includes('/convergence')) {
    disciplineId = 'convergence';
    pathType = 'pillar';
  }
  
  return { disciplineId, subdisciplineSlug, pathType };
}

function calculateNodeAccessibility(nodes: any[], userProgress: any, currentIndex: number) {
  let availableCount = 0;
  let lockedCount = 0;
  
  nodes.forEach((node, index) => {
    if (userProgress[node.node_id]?.completed) {
      // Completed nodes are neither available nor locked
      return;
    }
    
    // Simple sequential unlocking logic
    if (index === 0 || index <= currentIndex + 1) {
      // First node or within reach of current position
      availableCount++;
    } else {
      // Check if previous node is completed
      const previousNode = nodes[index - 1];
      if (userProgress[previousNode?.node_id]?.completed) {
        availableCount++;
      } else {
        lockedCount++;
      }
    }
  });
  
  return { availableCount, lockedCount };
}

function calculateRemainingTime(nodes: any[], userProgress: any, currentIndex: number) {
  return nodes
    .slice(currentIndex)
    .reduce((total, node) => {
      if (userProgress[node.node_id]?.completed) return total;
      
      const estimatedTime = node.estimated_study_time || 15;
      const timeSpent = userProgress[node.node_id]?.timeSpent || 0;
      
      return total + Math.max(0, estimatedTime - timeSpent);
    }, 0);
}

// Hook for components that need to declare themselves as part of a learning path
export const useDeclareLearningPath = (pathId: string, currentNodeId?: string, nodes?: any[]) => {
  // This hook can be used by components to provide learning path context
  // when they're not wrapped in a LearningPathProvider
  return {
    pathId,
    currentNodeId,
    nodes: nodes || []
  };
}; 